// FPLeagueHome.js
// Franchise Player — league lobby: my leagues, create a league, join a league.
// Mirrors UEContestHome.js (same tabs, invite-link handling and layout).
// Leagues are created by the fpCreateLeague Cloud Function, which prices the
// captain shop and sets the deadline server-side.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "./firebase";
import {
  collection, query, where, getDocs, doc, updateDoc, arrayUnion,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "./App";
import { FP_ACCENT, FP_ACCENT_DIM, STATUS_LABELS, leagueStatus, formatDeadline } from "./fp-theme";
import {
  FP_SEASON, CAPTAIN_PRICING, MAX_ENTRY_OPTIONS, WEEKLY_BUDGET,
  MAX_CAPTAIN_KICKBACK, CAPTAIN_SCORING_MULTIPLIER, formatMoney,
} from "./fp-config";

const FP_ADMIN_UID = "fy1HRZav9TW5MJFxHE1hc3mJFUl2";

// Approximate start of each 2026 NFL week (its Thursday), for the start-week
// picker. The server checks the real first kickoff when the league is created.
// The admin also gets the week already underway, for a test league.
function weekOptions(now = new Date(), isAdmin = false) {
  const week4Thursday = Date.UTC(2026, 9, 1); // Oct 1, 2026
  const out = [];
  for (let w = 1; w <= 18; w++) {
    const thu = new Date(week4Thursday + (w - 4) * 7 * 86400000);
    const label = thu.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
    if (thu.getTime() > now.getTime() + 86400000) {
      out.push({ week: w, label: `Week ${w} · starts ${label}` });
    } else if (isAdmin && thu.getTime() + 5 * 86400000 > now.getTime()) {
      out.push({ week: w, test: true, label: `Week ${w} · underway (admin test league)` });
    }
  }
  return out;
}

function Toggle({ label, description, value, onChange }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ flex: 1, marginRight: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "white" }}>{label}</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 2, lineHeight: 1.4 }}>{description}</div>
      </div>
      <button onClick={() => onChange(!value)} style={{
        width: 48, height: 26, borderRadius: 13, border: "none", cursor: "pointer",
        background: value ? FP_ACCENT : "rgba(255,255,255,0.15)",
        position: "relative", transition: "background 0.2s", flexShrink: 0,
      }}>
        <div style={{
          width: 20, height: 20, borderRadius: "50%", background: "white",
          position: "absolute", top: 3, left: value ? 25 : 3,
          transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }} />
      </button>
    </div>
  );
}

function OptionButton({ selected, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 16px", borderRadius: 8, border: "1px solid",
        borderColor: selected ? FP_ACCENT : "rgba(255,255,255,0.15)",
        background: selected ? FP_ACCENT_DIM : "transparent",
        color: selected ? FP_ACCENT : "rgba(255,255,255,0.55)",
        fontWeight: 700, fontSize: 14, cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

export default function FPLeagueHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [myLeagues, setMyLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("my");

  const isAdmin = user?.uid === FP_ADMIN_UID;
  const weeks = useMemo(() => weekOptions(new Date(), isAdmin), [isAdmin]);

  // Create form
  const [leagueName, setLeagueName] = useState("");
  const [leagueMessage, setLeagueMessage] = useState("");
  const [openInvite, setOpenInvite] = useState(true);
  const [maxEntries, setMaxEntries] = useState(1);
  const [pricing, setPricing] = useState("adp");
  const [startWeek, setStartWeek] = useState((weeks.find((w) => !w.test) || weeks[0])?.week || 1);
  const testMode = !!weeks.find((w) => w.week === startWeek)?.test;
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  // Join form
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState("");
  const [joining, setJoining] = useState(false);

  // Handle ?join=CODE invite links, plus the sessionStorage fallback used
  // when the query string is lost during the sign-in redirect.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let code = params.get("join");
    if (!code) {
      try {
        const stored = sessionStorage.getItem("fp_pendingJoinCode");
        if (stored) { code = stored; sessionStorage.removeItem("fp_pendingJoinCode"); }
      } catch (e) {}
    }
    if (code) {
      setJoinCode(code.toUpperCase());
      setTab("join");
    }
  }, [location.search]);

  useEffect(() => {
    fetchMyLeagues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMyLeagues = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "fp_leagues"), where("members", "array-contains", user.uid));
      const snap = await getDocs(q);
      setMyLeagues(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Error loading leagues:", e);
    }
    setLoading(false);
  };

  const createLeague = async () => {
    if (!leagueName.trim()) return;
    setCreating(true);
    setCreateError("");
    try {
      const result = await httpsCallable(getFunctions(), "fpCreateLeague")({
        name: leagueName.trim(),
        message: leagueMessage.trim(),
        openInvite,
        maxEntries,
        pricing,
        startWeek,
        testMode,
      });
      navigate(`league/${result.data.leagueId}`);
    } catch (e) {
      console.error("Error creating league:", e);
      setCreateError(e.message || "Couldn't create the league. Try again.");
    }
    setCreating(false);
  };

  const joinLeague = async () => {
    setJoinError("");
    const code = joinCode.toUpperCase().trim();
    if (!code) { setJoinError("Enter a join code."); return; }
    setJoining(true);
    try {
      const q = query(collection(db, "fp_leagues"), where("joinCode", "==", code));
      const snap = await getDocs(q);
      if (snap.empty) { setJoinError("No league found with that code."); setJoining(false); return; }
      const leagueDoc = snap.docs[0];
      const league = leagueDoc.data();

      if (league.members?.includes(user.uid)) {
        navigate(`league/${leagueDoc.id}`);
        setJoining(false);
        return;
      }
      if (leagueStatus(league) !== "shop_open") {
        setJoinError("This league's captain shop has closed, so it's no longer taking new managers.");
        setJoining(false);
        return;
      }

      await updateDoc(doc(db, "fp_leagues", leagueDoc.id), { members: arrayUnion(user.uid) });
      navigate(`league/${leagueDoc.id}`);
    } catch (e) {
      console.error("Error joining league:", e);
      setJoinError("Something went wrong. Try again.");
    }
    setJoining(false);
  };

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div style={{
        background: "linear-gradient(135deg, #0d1a20 0%, #11262e 100%)",
        border: "1px solid rgba(20,184,166,0.2)",
        borderRadius: 20, marginBottom: 24, overflow: "hidden", position: "relative",
      }}>
        <div style={{
          position: "absolute", inset: 0, opacity: 0.025,
          backgroundImage: "repeating-linear-gradient(90deg, white 0px, white 2px, transparent 2px, transparent 40px)",
          pointerEvents: "none",
        }} />
        <div style={{ padding: "28px 28px", position: "relative" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: FP_ACCENT, marginBottom: 6, textTransform: "uppercase" }}>
            {FP_SEASON} NFL Season
          </div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36, color: "white", letterSpacing: 2, lineHeight: 1, marginBottom: 10 }}>
            WELCOME BACK,&nbsp;<span style={{ color: FP_ACCENT }}>{(user.displayName || "MANAGER").toUpperCase()}</span>
          </div>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, marginBottom: 20, maxWidth: 540 }}>
            Buy one franchise player for the whole season, then fill the rest of your lineup every week with a {formatMoney(WEEKLY_BUDGET)} budget. Most total points wins.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn-fp btn-sm" onClick={() => setTab("create")}>+ Create League</button>
            <button className="btn btn-outline btn-sm" style={{ borderColor: FP_ACCENT, color: FP_ACCENT }} onClick={() => setTab("join")}>
              Join League
            </button>
          </div>
        </div>
      </div>

      {/* How it works strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 28 }}>
        {[
          { icon: "🎖️", label: "Buy a Franchise Player", detail: `Locked in all season, scores ${CAPTAIN_SCORING_MULTIPLIER}x` },
          { icon: "💵", label: "Spend Less, Get More", detail: `Unspent captain dollars add to your weekly budget, up to ${formatMoney(MAX_CAPTAIN_KICKBACK)}` },
          { icon: "📋", label: `${formatMoney(WEEKLY_BUDGET)} Weekly Budget`, detail: "Fill the other 8 lineup spots, D/ST included, every week" },
          { icon: "📈", label: "Total Points Wins", detail: "No matchups, the whole league is the field" },
        ].map((item) => (
          <div key={item.label} className="card" style={{ textAlign: "center", padding: "14px 12px", borderColor: "rgba(20,184,166,0.15)" }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{item.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "white", marginBottom: 3 }}>{item.label}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.4 }}>{item.detail}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="nav-tabs">
        {["my", "create", "join"].map((t) => (
          <button
            key={t}
            className={"nav-tab" + (tab === t ? " active" : "")}
            onClick={() => setTab(t)}
            style={tab === t ? { background: FP_ACCENT, color: "white" } : {}}
          >
            {t === "my" ? "My Leagues" : t === "create" ? "Create League" : "Join League"}
          </button>
        ))}
      </div>

      {/* My Leagues */}
      {tab === "my" && (
        <div>
          {loading && <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: 32 }}>Loading...</p>}
          {!loading && myLeagues.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 20px", color: "rgba(255,255,255,0.35)" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🏈</div>
              <p style={{ marginBottom: 16 }}>No leagues yet. Create or join one to get started.</p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                <button className="btn btn-fp btn-sm" onClick={() => setTab("create")}>Create a League</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setTab("join")}>Join a League</button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {myLeagues.map((l) => {
              const statusKey = leagueStatus(l);
              const status = STATUS_LABELS[statusKey];
              return (
                <div
                  key={l.id}
                  className="card"
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderLeft: `4px solid ${FP_ACCENT}`, cursor: "pointer", gap: 12 }}
                  onClick={() => navigate(`league/${l.id}`)}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 1 }}>
                      {l.name}{l.test && <span style={testBadge}>TEST</span>}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ color: status.color, fontWeight: 600 }}>
                        {status.label}{statusKey === "shop_open" ? ` · closes ${formatDeadline(l.shopDeadline)}` : ""}
                      </span>
                      <span>· {l.members?.length || 1} {l.members?.length === 1 ? "manager" : "managers"}</span>
                      {l.openInvite && statusKey === "shop_open" && (
                        <span>· Code: <span style={{ color: FP_ACCENT, letterSpacing: 2, fontWeight: 700 }}>{l.joinCode}</span></span>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn btn-fp btn-sm"
                    onClick={(e) => { e.stopPropagation(); navigate(`league/${l.id}`); }}
                  >
                    Enter →
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create League */}
      {tab === "create" && (
        <div className="card" style={{ padding: 28, maxWidth: 600 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: FP_ACCENT, letterSpacing: 2, marginBottom: 20 }}>
            CREATE A LEAGUE
          </div>

          <label style={labelStyle}>LEAGUE NAME</label>
          <input value={leagueName} onChange={(e) => setLeagueName(e.target.value)} placeholder="e.g. Sunday Franchise 2026" className="input fp-input" style={{ marginBottom: 16 }} maxLength={60} />

          <label style={labelStyle}>
            LEAGUE MESSAGE <span style={{ color: "rgba(255,255,255,0.2)", fontWeight: 400 }}>(optional)</span>
          </label>
          <textarea
            value={leagueMessage} onChange={(e) => setLeagueMessage(e.target.value)}
            placeholder="e.g. Buy-in is $50, Venmo @you. Weekly high score gets $10, season winner takes the rest."
            className="input fp-input" maxLength={500} style={{ minHeight: 80, resize: "vertical", lineHeight: 1.5, marginBottom: 20 }}
          />

          <label style={labelStyle}>FIRST WEEK</label>
          {weeks.length === 0 ? (
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 16 }}>No weeks left to start a league this season.</p>
          ) : (
            <select
              value={startWeek}
              onChange={(e) => setStartWeek(parseInt(e.target.value, 10))}
              className="input fp-input"
              style={{ marginBottom: 6 }}
            >
              {weeks.map((w) => <option key={w.week} value={w.week}>{w.label}</option>)}
            </select>
          )}
          <p style={{ fontSize: 12, color: testMode ? "#FBBF24" : "rgba(255,255,255,0.35)", marginBottom: 20 }}>
            {testMode
              ? "Test league: the shop closes at this week's next kickoff, and players whose games are already over are locked. Marked TEST."
              : "The captain shop closes at that week's first kickoff. Managers must join and buy their franchise player before then."}
          </p>

          <div style={{ ...labelStyle, marginBottom: 8 }}>CAPTAIN PRICES</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
            {Object.values(CAPTAIN_PRICING).map((opt) => (
              <button
                key={opt.id}
                onClick={() => setPricing(opt.id)}
                style={{
                  textAlign: "left", padding: "12px 14px", borderRadius: 10, cursor: "pointer",
                  border: `1px solid ${pricing === opt.id ? FP_ACCENT : "rgba(255,255,255,0.12)"}`,
                  background: pricing === opt.id ? FP_ACCENT_DIM : "transparent",
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 700, color: pricing === opt.id ? FP_ACCENT : "white" }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 2, lineHeight: 1.4 }}>{opt.description}</div>
              </button>
            ))}
          </div>

          <div style={{ ...labelStyle, marginBottom: 4 }}>LEAGUE SETTINGS</div>
          <div style={{ padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "white", marginBottom: 2 }}>Max Entries Per Person</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>Each entry buys its own franchise player and sets its own lineups.</div>
            <div style={{ display: "flex", gap: 8 }}>
              {MAX_ENTRY_OPTIONS.map((n) => (
                <OptionButton key={n} selected={maxEntries === n} onClick={() => setMaxEntries(n)}>{n}</OptionButton>
              ))}
            </div>
          </div>
          <Toggle
            label="Open Invites"
            description="Members can share the join code and invite others. Turn off to restrict invites to you only."
            value={openInvite}
            onChange={setOpenInvite}
          />

          {createError && <p className="error-text">{createError}</p>}

          <button
            onClick={createLeague}
            disabled={creating || !leagueName.trim() || weeks.length === 0}
            className="btn btn-fp btn-full btn-lg"
            style={{ borderRadius: 10, marginTop: 24 }}
          >
            {creating ? "Setting up your captain shop..." : "🎖️ Create League"}
          </button>
        </div>
      )}

      {/* Join League */}
      {tab === "join" && (
        <div className="card" style={{ padding: 28, maxWidth: 400 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: FP_ACCENT, letterSpacing: 2, marginBottom: 20 }}>
            JOIN A LEAGUE
          </div>
          <input
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && joinLeague()}
            placeholder="Enter 6-character code"
            className="input fp-input"
            style={{ marginBottom: 12, letterSpacing: 4, textTransform: "uppercase" }}
          />
          {joinError && <p className="error-text" style={{ marginBottom: 10 }}>{joinError}</p>}
          <button
            onClick={joinLeague}
            disabled={joining}
            className="btn btn-fp btn-full btn-lg"
            style={{ borderRadius: 10 }}
          >
            {joining ? "Looking up your league..." : "Join League →"}
          </button>
        </div>
      )}
    </div>
  );
}

const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 700,
  color: "rgba(255,255,255,0.4)", letterSpacing: 1, marginBottom: 6,
};

const testBadge = {
  fontFamily: "Inter, sans-serif", fontSize: 10, fontWeight: 800, letterSpacing: 1, marginLeft: 8,
  padding: "2px 6px", borderRadius: 4, color: "#FBBF24", border: "1px solid rgba(251,191,36,0.4)",
  background: "rgba(251,191,36,0.1)", verticalAlign: "middle",
};
